import React, { useState, useEffect } from 'react';
import EditableSection from './EditableSection';
import RichTextEditor from './RichTextEditor';
import MonthYearPicker from './MonthYearPicker';
import { Plus, Trash2, ChevronDown, ChevronUp } from 'lucide-react';

const formatDate = (dateStr) => {
  if (!dateStr) return '';
  const str = String(dateStr);
  if (str.toLowerCase() === 'present') return 'Present';
  const [year, month] = str.split('-');
  if (!year || !month) return str;
  const date = new Date(year, parseInt(month) - 1);
  return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
};

const renderDates = (start, end) => {
  if (!start && !end) return '';
  if (start && (!end || String(end).toLowerCase() === 'present')) return formatDate(start) + ' – Present';
  if (!start && end) return 'Until ' + formatDate(end);
  return `${formatDate(start)} – ${formatDate(end)}`;
};

const ExperienceEditor = ({ data, onSave, onRemove }) => {
  const [isEditing, setIsEditing] = useState(false);
  const [editData, setEditData] = useState(data || []);
  const [expandedIndex, setExpandedIndex] = useState(0); // keep first item expanded by default

  useEffect(() => {
    if (data) setEditData(data);
  }, [data]);

  const handleSave = () => {
    onSave(editData);
    setIsEditing(false);
  };

  const handleCancel = () => {
    setEditData(data || []);
    setIsEditing(false);
  };

  const addItem = () => {
    setEditData([...editData, { title: '', company: '', location: '', start_date: '', end_date: '', tech_stack: '', description: '' }]);
    setExpandedIndex(editData.length);
  };

  const updateItem = (index, field, value) => {
    const newData = [...editData];
    newData[index] = { ...newData[index], [field]: value };
    setEditData(newData);
  };

  const removeItem = (index) => {
    const newData = editData.filter((_, i) => i !== index);
    setEditData(newData);
    if (expandedIndex === index) {
      setExpandedIndex(Math.max(0, index - 1));
    } else if (expandedIndex > index) {
      setExpandedIndex(expandedIndex - 1);
    }
  };

  const toggleExpand = (index) => {
    if (expandedIndex === index) setExpandedIndex(-1);
    else setExpandedIndex(index);
  };

  // Ensure description is always an HTML string for the editor
  const renderDescription = (desc) => {
    if (Array.isArray(desc)) {
      if (desc.length === 0) return '';
      return '<ul><li>' + desc.join('</li><li>') + '</li></ul>';
    }
    return desc || '';
  };

  // Convert array back to original format if needed when saving? We just keep it as an HTML string.
  // The RichTextEditor handles lists gracefully.

  return (
    <EditableSection 
      title="Experience"
      isEditing={isEditing} 
      onEdit={() => setIsEditing(true)} 
      onCancel={handleCancel} 
      onSave={handleSave}
      onRemove={onRemove}
    >
      {isEditing ? (
        <div className="bg-gray-50 p-4 rounded-md border border-gray-200 font-sans">
          <div className="space-y-4 mb-4">
            {editData.map((exp, idx) => (
              <div key={idx} className="border border-gray-300 rounded bg-white overflow-hidden shadow-sm">
                <div 
                  className="bg-gray-100 px-4 py-3 flex justify-between items-center cursor-pointer hover:bg-gray-200 transition-colors"
                  onClick={() => toggleExpand(idx)}
                >
                  <div className="font-medium text-gray-800 flex-1 truncate">
                    {exp.title && exp.company ? `${exp.title} at ${exp.company}` : (exp.title || exp.company || 'New Experience')}
                  </div>
                  <div className="flex gap-2 items-center">
                    <button 
                      onClick={(e) => { e.stopPropagation(); removeItem(idx); }} 
                      className="text-red-500 hover:text-red-700 p-1 rounded hover:bg-red-50"
                      title="Remove Experience"
                    >
                      <Trash2 size={16} />
                    </button>
                    {expandedIndex === idx ? <ChevronUp size={20} className="text-gray-500" /> : <ChevronDown size={20} className="text-gray-500" />}
                  </div>
                </div>
                
                {expandedIndex === idx && (
                  <div className="p-4 space-y-4">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Job Title</label>
                        <input 
                          type="text" 
                          value={exp.title || ''} 
                          onChange={(e) => updateItem(idx, 'title', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          placeholder="e.g. Backend Developer"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Company</label>
                        <input 
                          type="text" 
                          value={exp.company || ''} 
                          onChange={(e) => updateItem(idx, 'company', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          placeholder="e.g. NEWME"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Location</label>
                        <input 
                          type="text" 
                          value={exp.location || ''} 
                          onChange={(e) => updateItem(idx, 'location', e.target.value)}
                          className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                          placeholder="e.g. Bengaluru / Remote"
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
                        <MonthYearPicker 
                          value={exp.start_date || ''} 
                          onChange={(val) => updateItem(idx, 'start_date', val)}
                        />
                      </div>
                      <div>
                        <div className="flex justify-between items-center mb-1">
                          <label className="block text-sm font-medium text-gray-700">End Date</label>
                          <label className="text-xs flex items-center gap-1 text-gray-600 cursor-pointer">
                            <input 
                              type="checkbox" 
                              checked={exp.end_date === 'Present'}
                              onChange={(e) => updateItem(idx, 'end_date', e.target.checked ? 'Present' : '')}
                            />
                            Present
                          </label>
                        </div>
                        <MonthYearPicker 
                          value={exp.end_date === 'Present' ? '' : (exp.end_date || '')} 
                          onChange={(val) => updateItem(idx, 'end_date', val)}
                          disabled={exp.end_date === 'Present'}
                        />
                      </div>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Tech Stack</label>
                      <input
                        type="text"
                        value={exp.tech_stack || ''}
                        onChange={(e) => updateItem(idx, 'tech_stack', e.target.value)}
                        className="w-full border border-gray-300 rounded px-3 py-2 text-sm focus:ring-1 focus:ring-blue-500 focus:outline-none"
                        placeholder="e.g. React, Node.js, PostgreSQL"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-1">Description (Bullets)</label>
                      <RichTextEditor 
                        value={renderDescription(exp.description)} 
                        onChange={(val) => updateItem(idx, 'description', val)} 
                        placeholder="Describe your responsibilities and achievements format as a bulleted list..."
                      />
                    </div>
                  </div>
                )}
              </div>
            ))}
            {editData.length === 0 && <p className="text-sm text-gray-500 italic py-2">No experience added yet.</p>}
          </div>
          <button onClick={addItem} className="text-blue-600 hover:text-blue-800 flex items-center gap-1 text-sm font-medium">
            <Plus size={16} /> Add Experience
          </button>
        </div>
      ) : (
        <div className="resume-content text-[15px] space-y-4">
          {(!data || data.length === 0) ? (
            <p className="text-gray-400 italic">No experience provided.</p>
          ) : (
            data.map((exp, idx) => (
              <div key={idx} className="mb-3">
                <div className="flex justify-between items-baseline mb-[2px]">
                  <div className="font-bold">
                    {exp.title} {exp.company && <span className="font-normal">&mdash; {exp.company}</span>}
                  </div>
                  {(exp.location || exp.dates || exp.start_date) && (
                    <div className="text-[13px] whitespace-nowrap ml-4 text-gray-700">
                      {exp.location}
                      {(exp.dates || exp.start_date) && (
                        <span className="italic">
                          {exp.location ? ' (' : ''}
                          {exp.dates ? exp.dates : renderDates(exp.start_date, exp.end_date)}
                          {exp.location ? ')' : ''}
                        </span>
                      )}
                    </div>
                  )}
                </div>
                {exp.tech_stack && exp.tech_stack.trim() !== '' && (
                  <div className="text-[13px] text-gray-900 mb-[2px]">
                    <span className="font-bold">Tech Stack: </span>
                    <span>{exp.tech_stack}</span>
                  </div>
                )}
                <div 
                  className="pl-4 prose-sm max-w-none text-gray-900 leading-snug style-quill-lists"
                  dangerouslySetInnerHTML={{ __html: renderDescription(exp.description) }}
                />
              </div>
            ))
          )}
        </div>
      )}
    </EditableSection>
  );
};

export default ExperienceEditor;
